import { useEffect, useRef, useState } from 'react'
import { pushBackEntry, popBackEntry } from './browserBackStack'

/**
 * Makes the browser (or hardware) Back button undo exactly this view when
 * it's open — pushes a history sentinel while `isOpen`, and runs `onBack`
 * when that sentinel is the one consumed by a Back press. Safe to call from
 * multiple components at once (see browserBackStack.ts for why a shared
 * stack, not one listener per caller, is what makes nesting safe).
 *
 * `onBack` doesn't need to be stable across renders — the latest version is
 * always the one invoked.
 *
 * It may return `'refused'` to decline the Back press — the #274 "don't throw
 * away what someone typed" rule, which browser Back was the third and only
 * unwired close path for. The history entry has already been consumed by the
 * time a handler runs, so refusing has to RE-ARM: the entry is forgotten and a
 * fresh one pushed on the next commit, restoring the level the press removed.
 * Done here rather than inside the shared stack's pop handler, so a refusal
 * uses the hook's own ordinary open path instead of pushing a new id into a
 * reconciliation that is mid-flight (see browserBackStack's module docs).
 */
export function useBrowserBackSentinel(
  isOpen: boolean,
  onBack: () => void | 'refused',
) {
  const idRef = useRef<number | null>(null)
  const onBackRef = useRef(onBack)
  // Bumped by a refusal purely to re-run the arming effect.
  const [rearm, setRearm] = useState(0)

  useEffect(() => {
    onBackRef.current = onBack
  })

  useEffect(() => {
    if (isOpen && idRef.current === null) {
      idRef.current = pushBackEntry(() => {
        // The stack has already dropped this entry by the time we run, so a
        // refusal only has to forget our id and ask for a re-push.
        if (onBackRef.current() === 'refused') {
          idRef.current = null
          setRearm((n) => n + 1)
        }
      })
    } else if (!isOpen && idRef.current !== null) {
      // In-app close: reclaim the history slot too, so this close doesn't
      // leave a stamp behind that swallows a later Back press. Safe even when
      // the close was itself caused by a Back press — popBackEntry no-ops on
      // an id the popstate handler already consumed.
      popBackEntry(idRef.current, { reclaimSlot: true })
      idRef.current = null
    }
  }, [isOpen, rearm])

  // Unmounting while still "open" (e.g. a hard navigation elsewhere) must
  // also release the entry so it doesn't linger and fire a stale callback.
  // Deliberately NO reclaimSlot here: StrictMode's dev-only simulated
  // unmount runs this cleanup between two mounts, and a history.back() fired
  // there would race the immediate re-push and close the view it re-opens.
  useEffect(() => {
    return () => {
      if (idRef.current !== null) {
        popBackEntry(idRef.current)
        idRef.current = null
      }
    }
  }, [])
}
